import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, FileText, TrendingUp } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Prevent redundant network calls when multiple <PublicStats /> are mounted
// on the same page. Simple in-memory cache lives for the SPA session.
let _statsCache = null;
let _inflight = null;

export const fetchStats = () => {
  if (_statsCache) return Promise.resolve(_statsCache);
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${BACKEND_URL}/api/public/stats`)
    .then((r) => {
      _statsCache = r.data;
      _inflight = null;
      return _statsCache;
    })
    .catch(() => {
      _inflight = null;
      return null;
    });
  return _inflight;
};

export const PublicStats = ({ variant = "auth" }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(_statsCache);
  const [showToday, setShowToday] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchStats().then((s) => {
      if (mounted && s) setStats(s);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Alternate every 4.5 s between the cumulative total and today's momentum.
  useEffect(() => {
    if (!stats || !stats.summaries_today) return;
    const id = setInterval(() => setShowToday((s) => !s), 4500);
    return () => clearInterval(id);
  }, [stats]);

  if (!stats) return null;
  if (stats.users_total === 0 && stats.summaries_total === 0) return null;

  const userLabel =
    stats.users_total === 1
      ? t("stats.users_one", { count: stats.users_total })
      : t("stats.users_other", { count: stats.users_total });
  const sumLabel =
    stats.summaries_total === 1
      ? t("stats.summaries_one", { count: stats.summaries_total })
      : t("stats.summaries_other", { count: stats.summaries_total });
  const todayCount = stats.summaries_today || 0;
  const canAlternate = todayCount > 0;
  const displayToday = showToday && canAlternate;
  const summariesIcon = displayToday ? TrendingUp : FileText;
  const SummariesIcon = summariesIcon;

  // ============ HERO VARIANT (landing pages: /about, /education, /etudiants) ============
  if (variant === "hero") {
    return (
      <div
        data-testid="public-stats-hero"
        className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-b border-border py-4"
      >
        <div className="flex items-center gap-2.5">
          <Users className="w-4 h-4 text-[#002FA7]" />
          <span className="text-sm">
            <strong className="font-mono font-bold tracking-tight">
              {stats.users_total.toLocaleString("fr-FR")}
            </strong>{" "}
            <span className="text-muted-foreground">
              {stats.users_total === 1
                ? t("stats.users_short_one", "utilisateur")
                : t("stats.users_short_other", "utilisateurs")}
            </span>
          </span>
        </div>
        <div className="w-px h-4 bg-border hidden sm:block" />
        <div className="flex items-center gap-2.5" data-testid="public-stats-hero-summaries">
          <SummariesIcon className={`w-4 h-4 ${displayToday ? "text-[#FF2A00]" : "text-[#002FA7]"}`} />
          {displayToday ? (
            <span key="today" className="text-sm animate-in fade-in slide-in-from-bottom-1 duration-500">
              <strong className="font-mono font-bold tracking-tight text-[#FF2A00]">
                +{todayCount.toLocaleString("fr-FR")}
              </strong>{" "}
              <span className="text-muted-foreground">résumés aujourd'hui</span>
            </span>
          ) : (
            <span key="total" className="text-sm animate-in fade-in slide-in-from-bottom-1 duration-500">
              <strong className="font-mono font-bold tracking-tight">
                {stats.summaries_total.toLocaleString("fr-FR")}
              </strong>{" "}
              <span className="text-muted-foreground">
                {stats.summaries_total === 1
                  ? t("stats.summaries_short_one", "résumé généré")
                  : t("stats.summaries_short_other", "résumés générés")}
              </span>
            </span>
          )}
        </div>
      </div>
    );
  }

  // ============ DEFAULT VARIANT (auth pages: /login, /signup) ============
  return (
    <div
      data-testid="public-stats"
      className="flex items-center gap-4 mt-8 pt-6 border-t border-border"
    >
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-[#002FA7]" />
        <span className="font-mono text-xs">{userLabel}</span>
      </div>
      <div className="w-px h-4 bg-border" />
      <div className="flex items-center gap-2" data-testid="public-stats-summaries">
        <SummariesIcon className={`w-4 h-4 ${displayToday ? "text-[#FF2A00]" : "text-[#002FA7]"}`} />
        {displayToday ? (
          <span key="today" className="font-mono text-xs animate-in fade-in slide-in-from-bottom-1 duration-500 text-[#FF2A00]">
            +{todayCount.toLocaleString("fr-FR")} aujourd'hui
          </span>
        ) : (
          <span key="total" className="font-mono text-xs animate-in fade-in slide-in-from-bottom-1 duration-500">
            {sumLabel}
          </span>
        )}
      </div>
    </div>
  );
};
